import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"
import L from "leaflet"

// Fix Leaflet default icon paths broken by bundlers
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
})

// Tracker map — live Leaflet map polling /companies/:id/tracker_map_data every 30s.
// Renders tracker markers (colour-coded by state) + geofence overlays.
// Alert sidebar is updated from the open_events JSON payload.
export default class extends Controller {
  static targets = ["mapCanvas", "alertList", "alertCount"]
  static values  = { dataUrl: String, acknowledgeUrlTemplate: String }

  connect() {
    this.map      = L.map(this.mapCanvasTarget, { zoomControl: true })
    this.markers  = {}      // { tracker_id: L.Marker }
    this.fenceLayers = []   // L.Layer[]

    L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
      attribution: "© <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors © <a href='https://carto.com/'>CARTO</a>",
      maxZoom: 19,
    }).addTo(this.map)

    // Default view — will auto-fit once data arrives
    this.map.setView([51.505, -0.09], 13)

    this.fetchAndUpdate()
    this.interval = setInterval(() => this.fetchAndUpdate(), 30_000)
  }

  disconnect() {
    clearInterval(this.interval)
  }

  async fetchAndUpdate() {
    try {
      const res  = await fetch(this.dataUrlValue, { headers: { Accept: "application/json" } })
      if (!res.ok) return
      const data = await res.json()
      this.updateTrackers(data.trackers  || [])
      this.updateFences(data.geofences   || [])
      this.updateAlerts(data.open_events || [])
    } catch (_err) {
      // Network errors are silently swallowed — next poll will retry
    }
  }

  // ── Tracker markers ────────────────────────────────────────────────────────

  updateTrackers(trackers) {
    const seen = new Set()

    trackers.forEach((t) => {
      if (t.lat == null || t.lng == null) return
      seen.add(t.id)

      const colour = this._trackerColour(t)
      const icon   = this._markerIcon(colour)
      const popup  = this._trackerPopup(t)

      if (this.markers[t.id]) {
        this.markers[t.id].setLatLng([t.lat, t.lng]).setIcon(icon).setPopupContent(popup)
      } else {
        const marker = L.marker([t.lat, t.lng], { icon })
          .bindPopup(popup)
          .addTo(this.map)
        this.markers[t.id] = marker
      }
    })

    // Remove stale markers
    Object.keys(this.markers).forEach((id) => {
      if (!seen.has(Number(id))) {
        this.markers[id].remove()
        delete this.markers[id]
      }
    })

    // Auto-fit on first load
    if (!this._hasFit && trackers.some((t) => t.lat != null)) {
      const latlngs = trackers.filter((t) => t.lat != null).map((t) => [t.lat, t.lng])
      if (latlngs.length > 0) {
        this.map.fitBounds(L.latLngBounds(latlngs).pad(0.2))
        this._hasFit = true
      }
    }
  }

  _trackerColour(t) {
    if (t.escalation_active) return "#f59e0b"   // amber — outside fence / escalating
    if (t.status !== "active") return "#9ca3af"  // grey — inactive
    return "#22c55e"                             // green — online and inside fence
  }

  _markerIcon(colour) {
    return L.divIcon({
      className: "",
      html: `<div style="
        width:14px;height:14px;border-radius:50%;
        background:${colour};border:2px solid white;
        box-shadow:0 1px 3px rgba(0,0,0,.4)"></div>`,
      iconSize:   [14, 14],
      iconAnchor: [7, 7],
      popupAnchor:[0, -10],
    })
  }

  _trackerPopup(tracker) {
    const battery = tracker.battery_pct != null ? `${tracker.battery_pct}%` : "—"
    const speed   = tracker.speed_kmh   != null ? `${tracker.speed_kmh} km/h` : "—"
    const seen    = tracker.last_reported_at ? new Date(tracker.last_reported_at).toLocaleTimeString() : "—"
    const esc     = tracker.escalation_active ? `<br><strong style='color:#f59e0b'>${t("javascript.controllers.tracker_map_controller.outside_geofence")}</strong>` : ""
    return `<strong>${tracker.name}</strong><br>
${t("javascript.controllers.tracker_map_controller.battery", { value: battery })}<br>
${t("javascript.controllers.tracker_map_controller.speed", { value: speed })}<br>
${t("javascript.controllers.tracker_map_controller.last_seen", { value: seen })}${esc}`
  }

  // ── Geofence overlays ──────────────────────────────────────────────────────

  updateFences(geofences) {
    this.fenceLayers.forEach((l) => l.remove())
    this.fenceLayers = []

    geofences.forEach((fence) => {
      const style = {
        color:       fence.color || "#ef4444",
        fillColor:   fence.color || "#ef4444",
        fillOpacity: 0.08,
        weight:      2,
      }

      let layer
      if (fence.kind === "circle" && fence.center_lat != null) {
        layer = L.circle([fence.center_lat, fence.center_lng], {
          radius: fence.radius_m,
          ...style,
        })
      } else if (fence.kind === "polygon") {
        // Geofence#to_leaflet_layer_json nests polygon coords under
        // polygon_geojson.coordinates — fall back to a top-level `coordinates`
        // key for safety, matching geofence_preview_controller.js.
        const coords = fence.polygon_geojson?.coordinates || fence.coordinates
        if (coords && coords[0]) {
          // GeoJSON polygon coords are [lng, lat] — Leaflet wants [lat, lng]
          const latlngs = coords[0].map(([lng, lat]) => [lat, lng])
          layer = L.polygon(latlngs, style)
        }
      }

      if (layer) {
        layer.bindTooltip(fence.name, { permanent: false, direction: "top" })
        layer.addTo(this.map)
        this.fenceLayers.push(layer)
      }
    })
  }

  // ── Alert sidebar ──────────────────────────────────────────────────────────

  updateAlerts(events) {
    if (!this.hasAlertListTarget) return

    if (this.hasAlertCountTarget) {
      this.alertCountTarget.textContent = events.length > 0
        ? t("javascript.controllers.tracker_map_controller.open_alerts_count", { count: events.length })
        : t("javascript.controllers.tracker_map_controller.no_open_alerts")
    }

    if (events.length === 0) {
      this.alertListTarget.innerHTML = `<div class="px-4 py-8 text-center"><p class="text-sm text-gray-400">${t("javascript.controllers.tracker_map_controller.no_open_alerts_list")}</p></div>`
      return
    }

    this.alertListTarget.innerHTML = events.map((ev) => {
      const ago = this._timeAgo(new Date(ev.occurred_at))
      return `<div class="px-4 py-3 border-b border-gray-100 text-sm" data-event-id="${ev.id}">
  <div class="flex items-start justify-between gap-2">
    <div>
      <span class="font-medium text-gray-900">${ev.tracker_name}</span>
      <span class="text-gray-500 text-xs ml-1">${this._eventTypeLabel(ev.event_type)}</span>
      <p class="text-xs text-gray-500 mt-0.5">${ev.fence_name}</p>
    </div>
    <span class="text-xs text-gray-400 whitespace-nowrap">${ago}</span>
  </div>
  <div class="mt-1.5">
    <button type="button" class="text-xs text-primary hover:underline bg-transparent border-0 cursor-pointer p-0"
      data-action="click->tracker-map#acknowledgeEvent" data-event-id="${ev.id}">${t("javascript.controllers.tracker_map_controller.acknowledge")}</button>
  </div>
</div>`
    }).join("")
  }

  // ── Acknowledge action (from the alert sidebar) ────────────────────────────

  async acknowledgeEvent(event) {
    const id = event.currentTarget.dataset.eventId
    if (!id || !this.hasAcknowledgeUrlTemplateValue) return

    const url = this.acknowledgeUrlTemplateValue.replace(":id", id)
    try {
      const res = await fetch(url, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
      })
      if (res.ok) this.fetchAndUpdate()
    } catch (_err) {
      // Next poll will retry / reflect the true state
    }
  }

  _eventTypeLabel(type) {
    // GeofenceEvent::EVENT_TYPES — translate the label, keep unknown values as-is.
    if (type === "entered") return t("javascript.controllers.tracker_map_controller.event_types.entered")
    if (type === "exited") return t("javascript.controllers.tracker_map_controller.event_types.exited")
    return type
  }

  _timeAgo(date) {
    const seconds = Math.floor((Date.now() - date) / 1000)
    if (seconds < 60)  return t("javascript.controllers.tracker_map_controller.seconds_ago", { count: seconds })
    if (seconds < 3600) return t("javascript.controllers.tracker_map_controller.minutes_ago", { count: Math.floor(seconds / 60) })
    return t("javascript.controllers.tracker_map_controller.hours_ago", { count: Math.floor(seconds / 3600) })
  }
}
